import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      console.log('🔍 AuthContext initializeAuth:', {
        hasToken: !!token,
        tokenLength: token?.length,
        hasSavedUser: !!savedUser,
        savedUserLength: savedUser?.length
      });
      
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          console.log('✅ AuthContext: Successfully parsed user data:', {
            username: userData.username,
            role: userData.role,
            hasId: !!userData._id
          });
          setUser(userData);
        } catch (parseError) {
          console.error('❌ AuthContext: Error parsing user data:', parseError);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('currentVendor');
        }
      } else {
        console.log('❌ AuthContext: Missing token or user data');
      }
    } catch (error) {
      console.error('❌ AuthContext: Error initializing auth:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentVendor');
    } finally {
      setIsLoading(false);
      console.log('🔄 AuthContext: Finished loading, isLoading set to false');
    }
  };

  const login = async (credentials: LoginRequest) => {
    try {
      console.log('🔍 Login attempt with credentials:', { 
        username: credentials.username
      });
      
      const response = await apiService.login(credentials);
      
      console.log('🔍 Login response:', response);
      
      // Handle the actual API response structure
      if (response.success && response.token && response.user) {
        localStorage.setItem('token', response.token);
        
        // Create a simplified user object that matches our User interface
        // Map 'admin' role to 'manager' for vendor admins
        const mappedRole = response.user.role === 'admin' ? 'manager' : response.user.role;
        const userData: User = {
          _id: response.user.id,
          username: response.user.username,
          fullName: response.user.fullName,
          role: mappedRole as 'super_admin' | 'reception' | 'operations' | 'accounting' | 'manager' | 'vendor',
          branch: {
            _id: response.user.branch.id,
            name: response.user.branch.name,
            code: response.user.branch.code,
            address: '',
            phone: '',
            manager: '',
            isActive: true,
            settings: {
              defaultPrices: {},
              workingHours: {}
            },
            createdAt: new Date()
          },
          permissions: response.user.permissions || [],
          phone: '',
          email: '',
          isActive: true,
          lastLogin: new Date(response.user.lastLogin),
          settings: {
            language: (response.user.settings?.language === 'en' ? 'en' : 'ar') as 'ar' | 'en',
            theme: (response.user.settings?.theme === 'dark' ? 'dark' : 'light') as 'light' | 'dark',
            notifications: response.user.settings?.notifications || {
              email: true,
              sms: false,
              whatsapp: true
            }
          },
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        
        // Redirect based on user role
        if (userData.role === 'super_admin') {
          window.location.href = '/';
        } else {
          window.location.href = '/vendor';
        }
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Extract error message from API response
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('خطأ في تسجيل الدخول');
      }
    }
  };

  const logout = () => {
    console.log('🚪 AuthContext: Logout called, clearing auth data');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentVendor');
    setUser(null);
    // Optionally call API logout endpoint
    apiService.logout().catch(console.error);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 